import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography, Card, message, Select, Row, Col, Skeleton } from 'antd';
import { VideoCameraOutlined, AudioOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  ConsoleLogger,
  LogLevel,
  DefaultMeetingSession,
  MeetingSessionConfiguration,
  DefaultDeviceController,
  DefaultStatsCollector,
} from 'amazon-chime-sdk-js';

import * as meetingActions from '../../../store/actions/MeetingActions';
import * as streamActions from '../../../store/actions/InterviewRecordingActions';
import * as actions from '../../../store/actions';
import analytics from '../../../services/analytics';

import { setCurrentInterviewPage } from '../../../store/actions';

import { useHistory } from 'react-router-dom';
import { isInterviewDeveloper } from '../../../helperFunctions';

import styles from '../../Assessment/MediaPermission/MediaPermissionComponent.scss';
import TestSound from '../Meeting/DeviceSelection/SpeakerDevices/TestSound';

import MicActivityBar from './MicActivityBar';

const { Title } = Typography;

if (DefaultStatsCollector && DefaultStatsCollector.prototype.getStatsWrapper) {
  DefaultStatsCollector.prototype.getStatsWrapper = function () {
    try {
      if (this.peerConnection && this.peerConnection.getStats) {
        return this.peerConnection.getStats();
      }
      return Promise.resolve(new Map());
    } catch (error) {
      console.warn('Stats collection failed, using fallback:', error);
      return Promise.resolve(new Map());
    }
  };
}

export default function ScreenShareModel({ interview_id }) {
  const { t } = useTranslation('interview_container');
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    videoStream,
    screenStream,
    hasVideo,
    hasScreen,
    error,
    videoInputs,
    audioInputs,
    audioOutputs,
    selectedVideoInput,
    selectedAudioInput,
    selectedAudioOutput,
    deviceController,
  } = useSelector(state => state.interviewRecording);

  const { meetingData, problems } = useSelector(state => state.assessment);
  const { participants, interview_type } = useSelector(state => state.interview);
  const localVideoRef = useRef(null);
  const [webrtcStream, setWebrtcStream] = useState(null);
  const [webrtcConnected, setWebrtcConnected] = useState(false);

  const isDeveloper = participants ? isInterviewDeveloper(participants) : false;

  const connectToWebRTC = async () => {
    console.log('Connecting to WebRTC stream for non-technical interview');
    try {
      const rtcConfigResp = await fetch('https://livetalking-api-qa.codehall.in/rtc-config');
      const rtcConfigData = await rtcConfigResp.json();

      const config = { sdpSemantics: 'unified-plan' };
      config.iceServers = rtcConfigData.iceServers || [];

      const pc = new RTCPeerConnection(config);

      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });

      pc.addEventListener('track', evt => {
        console.log('Received track:', evt.track.kind);
        if (evt.track.kind === 'video') {
          const stream = evt.streams[0] || new MediaStream([evt.track]);
          setWebrtcStream(stream);
          dispatch(streamActions.setWebrtcStream(stream));
          setWebrtcConnected(true);
        }
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering to complete before sending the offer
      await new Promise(resolve => {
        if (pc.iceGatheringState === 'complete') {
          resolve();
        } else {
          const checkState = () => {
            if (pc.iceGatheringState === 'complete') {
              pc.removeEventListener('icegatheringstatechange', checkState);
              resolve();
            }
          };
          pc.addEventListener('icegatheringstatechange', checkState);
        }
      });

      const gatheredOffer = pc.localDescription;
      const response = await fetch('https://livetalking-api-qa.codehall.in/offer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sdp: gatheredOffer.sdp, type: gatheredOffer.type }),
      });
      const answer = await response.json();
      console.log('WebRTC session:', answer.sessionid);

      await pc.setRemoteDescription(answer);

      dispatch(streamActions.setWebrtcSessionId(answer.sessionid));
    } catch (error) {
      console.error('Error connecting to WebRTC:', error);
      message.error('Failed to connect to AI interviewer');
    }
  };

  const initDevices = async () => {
    console.log('Initializing devices for interview. Interview type:', interview_type);
    try {
      const logLevel = process.env.NODE_ENV === 'production' ? LogLevel.ERROR : LogLevel.WARN;
      const logger = new ConsoleLogger('ChimeLogger', logLevel);
      const controller = new DefaultDeviceController(logger);

      await dispatch(streamActions.initDevices(controller));

      if (interview_type === 'NT' && isDeveloper) {
        await connectToWebRTC();
      } else {
        await dispatch(streamActions.initVideoStream());
      }
    } catch (error) {
      console.error('Device initialization error:', error);
      if (error.name === 'NotAllowedError') {
        message.error(t('media_permission.permission_denied'));
      } else if (error.name === 'NotFoundError') {
        message.error(t('media_permission.device_not_found'));
      } else if (error.name === 'NotReadableError') {
        message.error(t('media_permission.device_in_use'));
      } else {
        message.error(t('media_permission.init_failed'));
      }
    }
  };

  useEffect(() => {
    initDevices();
  }, []);

  useEffect(() => {
    if (interview_type === 'NT' && isDeveloper) {
      if (localVideoRef.current && webrtcStream) {
        localVideoRef.current.srcObject = webrtcStream;
      }
    } else {
      if (localVideoRef.current && videoStream) {
        localVideoRef.current.srcObject = videoStream;
      }
    }
  }, [videoStream, webrtcStream, interview_type, isDeveloper]);

  useEffect(() => {
    if (error) message.error(error);
  }, [error]);

  const handleScreenShare = async () => {
    try {
      dispatch(streamActions.setScreenShareInitiated());
      await dispatch(streamActions.initScreenStream());
      setTimeout(() => {
        dispatch(streamActions.clearScreenShareInitiated());
      }, 3000);
    } catch (error) {
      dispatch(streamActions.clearScreenShareInitiated());
    }
  };

  const handleDeviceChange = (kind, deviceId) => {
    dispatch(streamActions.setSelectedDevice(kind, deviceId));
  };

  const handleTestSpeaker = () => {
    if (selectedAudioOutput) {
      new TestSound(selectedAudioOutput);
    } else {
      message.info(t('media_permission.select_speaker_first'));
    }
  };

  const onContinue = async () => {
    // Prevent proceeding before participants load
    if (!participants) {
      return;
    }

    if (interview_type === 'NT' && isDeveloper) {
      if (!webrtcConnected) {
        message.error(t('media_permission.enable_camera_mic'));
        return;
      }
    } else {
      // For other interview types, check local camera
      if (!hasVideo || (!hasScreen && !isDeveloper)) {
        message.error(
          isDeveloper
            ? t('media_permission.enable_camera_mic')
            : t('media_permission.enable_all_devices'),
        );
        return;
      }
    }

    // Track that all required media permissions have been granted
    analytics.trackMediaPermissionsGranted(interview_id, {
      camera: true,
      microphone: true,
      screen_share: hasScreen || false,
    });

    if (isDeveloper) {
      try {
        const email_id = localStorage.getItem('username');
        console.log('email_id', email_id);
        const participant = participants.find(item => item.email === email_id);
        if (!participant) {
          message.error('Participant not found');
          return;
        }
        const participant_id = participant.id;

        const fetchedMeetingData = await dispatch(meetingActions.fetchMeeting(interview_id));

        // Check if meeting data was successfully fetched
        if (!fetchedMeetingData || !fetchedMeetingData.id) {
          message.error('Failed to fetch meeting data. Please try again.');
          return;
        }

        const attendeeData = await dispatch(
          actions.fetchAttendee(fetchedMeetingData.id, participant_id),
        );

        // Check if attendee data was successfully fetched
        if (!attendeeData || !attendeeData.aws_attendee_id) {
          message.error('Failed to fetch attendee data. Please try again.');
          return;
        }

        const configuration = new MeetingSessionConfiguration(
          {
            MeetingId: fetchedMeetingData.aws_meeting_id,
            ExternalMeetingId: String(fetchedMeetingData.id),
            MediaPlacement: fetchedMeetingData.aws_media_placement,
          },
          {
            AttendeeId: attendeeData.aws_attendee_id,
            ExternalUserId: String(attendeeData.participant_id),
            JoinToken: attendeeData.aws_join_token,
          },
        );

        const logger = new ConsoleLogger('ChimeLogger', LogLevel.WARN);
        const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

        if (interview_type === 'NT' && webrtcStream) {
          const videoTracks = webrtcStream.getVideoTracks();
          const audioTracks = webrtcStream.getAudioTracks();
          console.log(
            'WebRTC stream tracks - video:',
            videoTracks.length,
            'audio:',
            audioTracks.length,
          );
          if (videoTracks.length > 0) {
            console.log(
              'Video track state:',
              videoTracks[0].readyState,
              'enabled:',
              videoTracks[0].enabled,
            );
            await meetingSession.audioVideo.startVideoInput(new MediaStream(videoTracks));
          }
          if (audioTracks.length > 0) {
            await meetingSession.audioVideo.startAudioInput(new MediaStream(audioTracks));
          }
        } else {
          if (selectedAudioInput) {
            await meetingSession.audioVideo.startAudioInput(selectedAudioInput);
          }
          if (selectedVideoInput) {
            await meetingSession.audioVideo.startVideoInput(selectedVideoInput);
          }
        }

        dispatch(streamActions.startMeetingSession(meetingSession));
        console.log('let see the meetings data', fetchedMeetingData);
        dispatch(actions.createRecording(fetchedMeetingData.id));
        dispatch(setCurrentInterviewPage('meeting'));
      } catch (error) {
        console.error('Failed to start meeting session:', error);
        message.error('Failed to join meeting. Please try again.');
      }
    } else {
      try {
        const email_id = localStorage.getItem('username');
        const participant = participants.find(item => item.email === email_id);
        if (!participant) {
          message.error('Participant not found');
          return;
        }
        const participant_id = participant.id;

        const fetchedMeetingData = await dispatch(meetingActions.fetchMeeting(interview_id));
        console.log('fetchedMeetingData', fetchedMeetingData);

        if (fetchedMeetingData) {
          if (!fetchedMeetingData.id) {
            message.error('Invalid meeting data. Please try again.');
            dispatch(setCurrentInterviewPage('waiting_lobby'));
            return;
          }

          const attendeeData = await dispatch(
            actions.fetchAttendee(fetchedMeetingData.id, participant_id),
          );

          if (!attendeeData || !attendeeData.aws_attendee_id) {
            message.error('Failed to fetch attendee data. Please try again.');
            dispatch(setCurrentInterviewPage('waiting_lobby'));
            return;
          }

          const configuration = new MeetingSessionConfiguration(
            {
              MeetingId: fetchedMeetingData.aws_meeting_id,
              ExternalMeetingId: String(fetchedMeetingData.id),
              MediaPlacement: fetchedMeetingData.aws_media_placement,
            },
            {
              AttendeeId: attendeeData.aws_attendee_id,
              ExternalUserId: String(attendeeData.participant_id),
              JoinToken: attendeeData.aws_join_token,
            },
          );

          const logger = new ConsoleLogger('ChimeLogger', LogLevel.WARN);
          const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);

          if (selectedAudioInput) {
            await meetingSession.audioVideo.startAudioInput(selectedAudioInput);
          }
          if (selectedVideoInput) {
            await meetingSession.audioVideo.startVideoInput(selectedVideoInput);
          }
          dispatch(streamActions.startMeetingSession(meetingSession));

          setTimeout(async () => {
            if (screenStream) {
              await meetingSession.audioVideo.startContentShare(screenStream);
            }
          }, 3000);
          dispatch(setCurrentInterviewPage('meeting'));
        } else {
          dispatch(setCurrentInterviewPage('waiting_lobby'));
        }
      } catch (error) {
        console.error('Failed to fetch or join meeting:', error);
        dispatch(setCurrentInterviewPage('waiting_lobby'));
      }
    }
  };

  return (
    <div className={styles['media-permission-container']}>
      <Card className={styles['media-permission-card']}>
        {!participants ? (
          <>
            <div className={styles['media-permission-header']}>
              <Skeleton
                active
                title={{ width: '55%' }}
                paragraph={false}
              />
              <div style={{ marginTop: 8 }}>
                <Skeleton
                  active
                  title={false}
                  paragraph={{ rows: 2, width: ['95%', '75%'] }}
                />
              </div>
            </div>

            <div className={styles['media-permission-content']}>
              <Row gutter={32}>
                <Col
                  span={12}
                  className={styles['media-permission-left-column']}
                >
                  <div className={styles['media-permission-section']}>
                    <Skeleton.Button
                      active
                      block
                      style={{ height: 32, marginBottom: 12 }}
                    />
                    <div
                      className={styles['media-permission-video-container']}
                      style={{ marginBottom: 16 }}
                    >
                      <div
                        className={styles['media-permission-video']}
                        style={{
                          width: '100%',
                          paddingTop: '56.25%',
                          background: '#f5f5f5',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <Skeleton.Input
                      active
                      block
                      style={{ height: 32, marginBottom: 12 }}
                    />
                    <Skeleton.Button
                      active
                      block
                      style={{ height: 32, marginTop: 8 }}
                    />
                  </div>
                </Col>

                <Col
                  span={12}
                  className={styles['media-permission-right-column']}
                >
                  <div className={styles['media-permission-mic-section']}>
                    <Skeleton.Button
                      active
                      block
                      style={{ height: 32, marginBottom: 12 }}
                    />
                    <Skeleton.Input
                      active
                      block
                      style={{ height: 32, marginBottom: 12 }}
                    />
                    <Skeleton.Input
                      active
                      block
                      style={{ height: 24, marginBottom: 24 }}
                    />
                    <Skeleton.Button
                      active
                      block
                      style={{ height: 32, marginBottom: 12 }}
                    />
                    <Skeleton.Input
                      active
                      block
                      style={{ height: 32, marginBottom: 12 }}
                    />
                    <Skeleton.Button
                      active
                      style={{ width: 160, height: 32 }}
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </>
        ) : (
          <>
            <div className={styles['media-permission-header']}>
              <Title
                level={3}
                className={styles['media-permission-title']}
              >
                {isDeveloper
                  ? t('media_permission.camera_mic_title')
                  : t('media_permission.camera_mic_screen_share_title')}
              </Title>
              <Typography.Text className={styles['media-permission-subtitle']}>
                {isDeveloper
                  ? t('media_permission.camera_mic_subtitle')
                  : t('media_permission.camera_mic_screen_share_subtitle')}
              </Typography.Text>
            </div>

            <div className={styles['media-permission-content']}>
              <Row gutter={32}>
                <Col
                  span={12}
                  className={styles['media-permission-left-column']}
                >
                  <div className={styles['media-permission-section']}>
                    <div className={styles['media-permission-icon-section']}>
                      <VideoCameraOutlined style={{ marginRight: 8, color: '#666' }} />
                      <Typography.Text strong>{t('media_permission.camera')}</Typography.Text>
                    </div>

                    <div className={styles['media-permission-video-container']}>
                      <video
                        ref={localVideoRef}
                        className={styles['media-permission-video']}
                        autoPlay
                        muted
                        playsInline
                      />
                      {!webrtcConnected && interview_type === 'NT' && (
                        <div className={styles['media-permission-video-overlay']}>
                          <VideoCameraOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                          <div>Connecting to AI interviewer...</div>
                        </div>
                      )}
                      {!hasVideo && interview_type !== 'NT' && (
                        <div className={styles['media-permission-video-overlay']}>
                          <VideoCameraOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                          <div>{t('media_permission.camera_preview')}</div>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className={styles['media-permission-label']}>
                        {t('media_permission.camera_device')}
                      </label>
                      <Select
                        className={styles['media-permission-select']}
                        placeholder={t('media_permission.select_camera')}
                        value={selectedVideoInput || undefined}
                        onChange={value => handleDeviceChange('video', value)}
                      >
                        {videoInputs.map(device => (
                          <Select.Option
                            key={device.deviceId}
                            value={device.deviceId}
                          >
                            {device.label || `Camera (${device.deviceId})`}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>

                    {!isDeveloper && (
                      <div style={{ marginTop: '5%' }}>
                        <div className={styles['media-permission-icon-section']}>
                          <ShareAltOutlined style={{ marginRight: 8, color: '#666' }} />
                          <Typography.Text strong>
                            {t('media_permission.screen_share')}
                          </Typography.Text>
                        </div>
                        <div className={styles['media-permission-screen-section']}>
                          <Button
                            type="primary"
                            className={styles['media-permission-button']}
                            onClick={handleScreenShare}
                            disabled={hasScreen}
                          >
                            {hasScreen
                              ? t('media_permission.screen_enabled')
                              : t('media_permission.allow_screen')}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Col>

                <Col
                  span={12}
                  className={styles['media-permission-right-column']}
                >
                  <div className={styles['media-permission-mic-section']}>
                    <div className={styles['media-permission-icon-section']}>
                      <AudioOutlined style={{ marginRight: 8, color: '#666' }} />
                      <Typography.Text strong>{t('media_permission.microphone')}</Typography.Text>
                    </div>

                    <div className={styles['media-permission-section']}>
                      <label className={styles['media-permission-label']}>
                        {t('media_permission.microphone_device')}
                      </label>
                      <Select
                        className={styles['media-permission-select']}
                        placeholder={t('media_permission.select_microphone')}
                        value={selectedAudioInput || undefined}
                        onChange={value => handleDeviceChange('audio', value)}
                      >
                        {audioInputs.map(device => (
                          <Select.Option
                            key={device.deviceId}
                            value={device.deviceId}
                          >
                            {device.label || `Microphone (${device.deviceId})`}
                          </Select.Option>
                        ))}
                      </Select>
                      <div style={{ marginTop: '1rem' }}>
                        <label className={styles['media-permission-label']}>
                          {t('media_permission.microphone_activity')}
                        </label>
                        <MicActivityBar audioStream={videoStream ? videoStream : null} />
                      </div>
                    </div>

                    <div style={{ marginTop: '2rem' }}>
                      <div className={styles['media-permission-icon-section']}>
                        <AudioOutlined style={{ marginRight: 8, color: '#666' }} />
                        <Typography.Text strong>{t('media_permission.speaker')}</Typography.Text>
                      </div>
                      <div>
                        <label className={styles['media-permission-label']}>
                          {t('media_permission.speaker_device')}
                        </label>
                        <Select
                          className={styles['media-permission-select']}
                          placeholder={t('media_permission.select_speaker')}
                          value={selectedAudioOutput || undefined}
                          onChange={value => handleDeviceChange('audiooutput', value)}
                          style={{ marginBottom: '1rem' }}
                        >
                          {audioOutputs.map(device => (
                            <Select.Option
                              key={device.deviceId}
                              value={device.deviceId}
                            >
                              {device.label || `Speaker (${device.deviceId})`}
                            </Select.Option>
                          ))}
                        </Select>
                      </div>
                      <Button
                        type="default"
                        onClick={handleTestSpeaker}
                        disabled={!selectedAudioOutput}
                        style={{ display: 'flex' }}
                      >
                        {t('media_permission.test_speakers')}
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <Button
              type="default"
              size="large"
              disabled={
                (interview_type === 'NT' && isDeveloper ? !webrtcConnected : !hasVideo) ||
                (!hasScreen && !isDeveloper)
              }
              onClick={onContinue}
              style={{ marginTop: '5%' }}
            >
              {t('media_permission.continue')}
            </Button>
          </>
        )}
      </Card>
    </div>
  );
}
